import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { PromoFailReason } from "ipctg-inflow-types/InFlowEnums"
import {
  GatedPromoPaymentPOSTRequest,
  GatedPromoPaymentPOSTSuccessResponse,
} from "ipctg-inflow-types/InFlowGatedAPI"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import TextField from "@/globalComponents/molecules/TextField"
import { cleanEmptyParams } from "@/utilities/etc"
import {
  ExpectedError,
  GatedPromoPaymentError,
} from "@/utilities/expectedErrors"
import {
  checkoutQueryOptions,
  paymentIntentQueryOptions,
} from "@/utilities/hooks/queries/payment"
import { registerQueryOptions } from "@/utilities/hooks/queries/shared"
import { prettyCurrencyNumber } from "@/utilities/normalizers"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/$pamID/$sessionID/cart"
)({
  validateSearch: (search: Record<string, unknown>): { from?: string } => {
    return cleanEmptyParams({
      from: (search.from as string) || "",
    })
  },
  beforeLoad: async ({ context, params }) => {
    await context.queryClient.fetchQuery(
      registerQueryOptions({
        sessionID: params.sessionID,
        phone: null,
        isInLane: true,
        isReregistering: false,
      })
    )
  },
  component: RouteComponent,
})

interface DiscountCodeSchema {
  promoCode: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { t } = useTranslation()
  const { qrLookupID, sessionID, pamID } = Route.useParams()
  const queryClient = useQueryClient()
  const { data: checkout } = useSuspenseQuery(
    checkoutQueryOptions({ sessionID })
  )
  const [discountCodeApplied, setDiscountCodeApplied] = useState("")
  const form = useForm<DiscountCodeSchema>({
    defaultValues: {
      promoCode: "",
    },
  })
  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })
  const [isLoading, setLoading] = useState(false)

  return (
    <>
      <Card>
        <Heading level={1} as={1}>
          {t("inlane.cart.header")}
        </Heading>
        <dl>
          <div>
            <dt>{checkout.transactionDetails.rateName}</dt>
            <dd>
              {prettyCurrencyNumber(checkout.transactionDetails.baseRate)}
            </dd>
          </div>
          {!!checkout.transactionDetails.convenienceFee && (
            <div>
              {/* TODO need to have this label be based on the checkout object. Not currently supported */}
              <dt>{t("inlane.cart.list.convenienceFee")}</dt>
              <dd className="bold">
                {prettyCurrencyNumber(
                  checkout.transactionDetails.convenienceFee
                )}
              </dd>
            </div>
          )}
          {!!checkout.transactionDetails.tax && (
            <div>
              <dt>{t("inlane.cart.list.tax")}</dt>
              <dd>{prettyCurrencyNumber(checkout.transactionDetails.tax)}</dd>
            </div>
          )}
          {!!checkout.transactionDetails.computedDiscount && (
            <div style={{ color: "var(--success-11)" }}>
              <dt className="bold">{t("inlane.cart.list.discount")}</dt>
              <dd className="bold">
                -
                {prettyCurrencyNumber(
                  checkout.transactionDetails.computedDiscount
                )}
              </dd>
            </div>
          )}
        </dl>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {!checkout.transactionDetails.computedDiscount && (
            <fieldset disabled={isPending}>
              <Controller<DiscountCodeSchema>
                name="promoCode"
                control={form.control}
                render={({
                  field: { ref, onChange, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    {...rest}
                    onChange={(v) => onChange(v.toUpperCase())}
                    isInvalid={invalid}
                  >
                    <Label>{t("inlane.cart.fieldLabel")}</Label>
                    <div className="promoCodeField field--input">
                      {/* Width 0 to avoid input overflow issues */}
                      <TextField.Input innerRef={ref} style={{ width: 0 }} />
                      <Button
                        type="submit"
                        hue="neutral"
                        variant="default"
                        isPending={isPending}
                        style={{ width: "fit-content", flexShrink: 0 }}
                      >
                        {t("inlane.cart.apply")}
                      </Button>
                    </div>
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </fieldset>
          )}
          {discountCodeApplied && (
            <Text level={2} className="highlighted-cell">
              <b>{discountCodeApplied}</b>{" "}
              {t("inlane.cart.successfullyApplied")}
            </Text>
          )}
        </form>
        <Separator
          orientation="horizontal"
          style={{ marginTop: "var(--space-5)" }}
        />
        <dl>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <dt>
              <Heading level={2}>{t("inlane.cart.due")}</Heading>
            </dt>
            <dd>
              <p className="headline--2">
                {prettyCurrencyNumber(checkout.transactionDetails.amountDue)}
              </p>
            </dd>
          </div>
        </dl>
        <Separator
          orientation="horizontal"
          style={{ marginTop: "var(--space-5)" }}
        />
        <Button
          hue="secondary"
          variant="default"
          isPending={isPending || isLoading}
          onPress={async () => {
            // TODO: Fix me, we don't currently allow clearing promo codes so this logic will likely need to change
            setLoading(true)
            try {
              const promoCode = form.getValues("promoCode")
              if (promoCode && promoCode !== discountCodeApplied) {
                await mutateAsync({ promoCode })
              }
              await navigate({
                to: "/qr/$qrLookupID/gated/$pamID/$sessionID/checkout",
              })
            } finally {
              setLoading(false)
            }
          }}
        >
          {t("inlane.cart.submit")}
          <Icon name="arrowRight" slot="right" />
        </Button>
      </Card>
      <Text level={3} className="center">
        <TermsAndConditions qrLookupID={qrLookupID} />
      </Text>
    </>
  )

  function onSubmit(data: DiscountCodeSchema) {
    // I don't think we really need an error state if they didn't submit a promo code
    if (data.promoCode) {
      mutate(data)
    }
  }

  async function mutationFn(request: GatedPromoPaymentPOSTRequest) {
    const rsp = await api.promoPayment.POST(request)
    if ("failReason" in rsp) {
      const shouldThrow = [
        PromoFailReason.InvalidRequestFormat,
        PromoFailReason.SystemError,
        PromoFailReason.Unauthorized,
      ]
      const error = new GatedPromoPaymentError(rsp.failReason)
      if (shouldThrow.some((r) => r === rsp.failReason)) {
        throw error
      }
      form.setError("promoCode", { message: t(error.translationKey) })
      return
    }
    return rsp
  }

  function onSuccess(
    rsp: GatedPromoPaymentPOSTSuccessResponse | undefined,
    variables: GatedPromoPaymentPOSTRequest
  ) {
    if (!rsp) return
    queryClient.setQueryData(checkoutQueryOptions({ sessionID }).queryKey, rsp)
    queryClient.removeQueries({
      ...paymentIntentQueryOptions({ sessionID, pamID }),
    })
    setDiscountCodeApplied(variables.promoCode)
  }

  function onError(error: Error) {
    if (error instanceof ExpectedError) {
      form.setError("promoCode", { message: t(error.translationKey) })
    } else {
      form.setError("promoCode", { message: error.message })
    }
  }
}
